import { CopilotPromptAudience, CopilotPromptLanguage, ICopilotLibraryName, ICopilotPromptDto, ICopilotPromptInput } from "../../data-structures/interfaces";

// Pure helpers for the Copilot prompt editors (CopilotPromptsPanel and the /copilot-prompts page).
// The limits mirror the API's CopilotPromptValidation (spec 2026-10-13-copilot-chat-design.md §3.3);
// the API checks them again.

export const COPILOT_PROMPT_LIMITS = {
    title: 100,
    text: 1500,
    guidance: 4000,
    libraries: 20,
    libraryName: 100,
    /** The stored JSON array of library names is NVARCHAR(1000). */
    librariesJson: 1000,
    tenantPrompts: 50,
};

export const COPILOT_PROMPT_LANGUAGES: CopilotPromptLanguage[] = ["en", "nl", "de"];

/** The fields the editor marks; "libraries" covers every library rule. */
export type CopilotPromptInputError = "title" | "text" | "guidance" | "libraries" | "audience";

/** Replaces "{0}", "{1}", ... in a localized template. */
export const formatString = (template: string, ...args: (string | number)[]): string =>
    args.reduce<string>((text, arg, index) => text.split(`{${index}}`).join(String(arg)), template);

/** Trimmed, without empty entries, the first spelling of each name (ignoring case) kept. */
export const normalizeLibraryNames = (names: (string | null | undefined)[] | null | undefined): string[] => {
    const seen: string[] = [];
    const result: string[] = [];
    (names ?? []).forEach(raw => {
        const name = typeof raw === "string" ? raw.trim() : "";
        const key = name.toLowerCase();
        if (name && seen.indexOf(key) < 0) {
            seen.push(key);
            result.push(name);
        }
    });
    return result;
};

/**
 * The global library-names answer (2026-10-21): each name once, as normalizeLibraryNames keeps
 * them, with the names of the template configurations that have it, sorted by name. Items without
 * a name are left out; an answer that is not a list is no names.
 */
export const parseCopilotLibraryNames = (raw: unknown): ICopilotLibraryName[] => {
    if (!Array.isArray(raw)) {
        return [];
    }
    const seen: string[] = [];
    const result: ICopilotLibraryName[] = [];
    raw.forEach((item: any) => {
        const name = item && typeof item.name === "string" ? item.name.trim() : "";
        if (!name || seen.indexOf(name.toLowerCase()) >= 0) {
            return;
        }
        seen.push(name.toLowerCase());
        result.push({ name, configurations: Array.isArray(item.configurations) ? normalizeLibraryNames(item.configurations) : [] });
    });
    return result.sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * The length of the JSON the API stores for these names (CopilotPromptValidation.SerializeLibraries,
 * NVARCHAR(1000)): JSON.stringify's, plus 5 for each U+0085, U+2028 and U+2029, which the API writes
 * as a six-character escape. 0 when there are none: the API stores NULL.
 */
export const storedLibrariesLength = (names: string[]): number => {
    if (names.length === 0) return 0;
    const json = JSON.stringify(names);
    let length = json.length;
    for (let index = 0; index < json.length; index++) {
        const code = json.charCodeAt(index);
        if (code === 0x85 || code === 0x2028 || code === 0x2029) length += 5;
    }
    return length;
};

/** The fields that break a rule; empty when the input can be saved. */
export const validateCopilotPromptInput = (input: ICopilotPromptInput, isGlobal: boolean): CopilotPromptInputError[] => {
    const errors: CopilotPromptInputError[] = [];
    const title = (input.title ?? "").trim();
    if (!title || title.length > COPILOT_PROMPT_LIMITS.title) errors.push("title");
    const text = (input.text ?? "").trim();
    if (!text || text.length > COPILOT_PROMPT_LIMITS.text) errors.push("text");
    if ((input.guidance ?? "").trim().length > COPILOT_PROMPT_LIMITS.guidance) errors.push("guidance");
    const libraries = normalizeLibraryNames(input.defaultLibraries);
    if (libraries.length > COPILOT_PROMPT_LIMITS.libraries
        || libraries.some(name => name.length > COPILOT_PROMPT_LIMITS.libraryName)
        || storedLibrariesLength(libraries) > COPILOT_PROMPT_LIMITS.librariesJson) {
        errors.push("libraries");
    }
    if (isGlobal && input.audience !== 0 && input.audience !== 1) errors.push("audience");
    return errors;
};

/** The body to send: trimmed, blank guidance as null, libraries normalized, no tenant audience. */
export const cleanCopilotPromptInput = (input: ICopilotPromptInput, isGlobal: boolean): ICopilotPromptInput => ({
    title: (input.title ?? "").trim(),
    text: (input.text ?? "").trim(),
    guidance: input.guidance && input.guidance.trim() ? input.guidance.trim() : null,
    defaultLibraries: normalizeLibraryNames(input.defaultLibraries),
    language: input.language ?? null,
    requiresScope: input.requiresScope,
    isEnabled: input.isEnabled,
    audience: isGlobal ? input.audience : null,
});

/** A new prompt: enabled, only with a location, every language; global ones for accountancy tenants. */
export const emptyCopilotPromptInput = (isGlobal: boolean): ICopilotPromptInput => ({
    title: "",
    text: "",
    guidance: null,
    defaultLibraries: [],
    language: null,
    requiresScope: true,
    isEnabled: true,
    audience: isGlobal ? 1 : null,
});

export const toCopilotPromptInput = (prompt: ICopilotPromptDto): ICopilotPromptInput => ({
    title: prompt.title,
    text: prompt.text,
    guidance: prompt.guidance,
    defaultLibraries: prompt.defaultLibraries.slice(),
    language: prompt.language,
    requiresScope: prompt.requiresScope,
    isEnabled: prompt.isEnabled,
    audience: prompt.isGlobal ? prompt.audience : null,
});

/** One prompt from the API, which leaves out null members; null when the item is unusable. */
export const parseCopilotPromptDto = (raw: any): ICopilotPromptDto | null => {
    if (!raw || typeof raw.id !== "number" || typeof raw.title !== "string" || typeof raw.text !== "string") {
        return null;
    }
    return {
        id: raw.id,
        title: raw.title,
        text: raw.text,
        guidance: typeof raw.guidance === "string" ? raw.guidance : null,
        defaultLibraries: Array.isArray(raw.defaultLibraries) ? raw.defaultLibraries.filter((name: unknown) => typeof name === "string") : [],
        language: COPILOT_PROMPT_LANGUAGES.indexOf(raw.language) >= 0 ? raw.language : null,
        requiresScope: raw.requiresScope !== false,
        isGlobal: raw.isGlobal === true,
        isEnabled: raw.isEnabled !== false,
        sortOrder: typeof raw.sortOrder === "number" ? raw.sortOrder : 0,
        audience: raw.audience === 0 || raw.audience === 1 ? (raw.audience as CopilotPromptAudience) : null,
        isHidden: raw.isHidden === true,
    };
};

export const parseCopilotPromptDtos = (data: any): ICopilotPromptDto[] =>
    Array.isArray(data)
        ? data.map(parseCopilotPromptDto).filter((prompt): prompt is ICopilotPromptDto => prompt !== null)
        : [];

/** The ids in their new order after moving one prompt a place up (-1) or down (1); null past either end. */
export const movePromptIds = (prompts: { id: number }[], index: number, delta: -1 | 1): number[] | null => {
    const target = index + delta;
    if (index < 0 || index >= prompts.length || target < 0 || target >= prompts.length) {
        return null;
    }
    const ids = prompts.map(prompt => prompt.id);
    const moved = ids[index];
    ids[index] = ids[target];
    ids[target] = moved;
    return ids;
};

/**
 * Default-library suggestions: the typed name first (any library name may be entered, also one the
 * Portal does not know), then the known names that contain it. Names already chosen are left out.
 */
export const librarySuggestions = (filter: string, names: string[], selected: string[]): string[] => {
    const text = (filter ?? "").trim();
    const lower = text.toLowerCase();
    const taken = (selected ?? []).map(name => name.toLowerCase());
    const matches = (names ?? []).filter(name =>
        taken.indexOf(name.toLowerCase()) < 0 && (!lower || name.toLowerCase().indexOf(lower) >= 0));
    const known = taken.indexOf(lower) >= 0 || (names ?? []).some(name => name.toLowerCase() === lower);
    return text && !known ? [text].concat(matches) : matches;
};
